import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Search, ArrowUpDown, CheckCircle2, Gem, Delete } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import AnimatedButton from '../../../components/AnimatedButton'
import useAudioManager from '../../../hooks/useAudioManager'

const BetModal = ({
  isOpen,
  onClose,
  onSubmit,
  onLoadInventory,
  inventory = [],
  balance = 0,
  isLoading = false
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const MIN_BET_TOTAL = 100
  const [crystalValue, setCrystalValue] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [errorText, setErrorText] = useState('')
  const { playSound } = useAudioManager({
    pick: 'pick'
  })

  useEffect(() => {
    if (isOpen && onLoadInventory) {
      onLoadInventory()
    }
  }, [isOpen, onLoadInventory])

  useEffect(() => {
    if (!isOpen) {
      setCrystalValue('')
      setSelectedIds([])
      setSearchQuery('')
      setSortOrder('desc')
      setErrorText('')
    }
  }, [isOpen])

  const normalizeCrystalValue = (value) => {
    if (value === '' || value === null || typeof value === 'undefined') {
      return ''
    }

    const numericValue = Number(value)
    const safeBalance = Math.max(0, Number(balance || 0))

    if (!Number.isFinite(numericValue)) {
      return ''
    }

    return String(Math.max(0, Math.min(numericValue, safeBalance)))
  }

  const parsedCrystal = Number(crystalValue || 0)

  const toggleItem = (itemId) => {
    playSound('pick')
    setSelectedIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    )
  }

  const handleQuickCrystal = (value) => {
    playSound('pick')
    setCrystalValue(normalizeCrystalValue(value))
  }

  const selectedItems = useMemo(() => {
    return inventory.filter((item) => selectedIds.includes(item.id))
  }, [inventory, selectedIds])

  const itemsTotal = useMemo(() => {
    return selectedItems.reduce((sum, item) => sum + Number(item.price ?? item.cost ?? 0), 0)
  }, [selectedItems])

  const totalBet = parsedCrystal + itemsTotal
  const missingAmount = Math.max(0, MIN_BET_TOTAL - totalBet)
  const canSubmit = totalBet >= MIN_BET_TOTAL

  const filteredInventory = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    const baseList = normalizedQuery
      ? inventory.filter((item) => item.name.toLowerCase().includes(normalizedQuery))
      : inventory

    return [...baseList].sort((a, b) => {
      const priceA = Number(a.cost || 0)
      const priceB = Number(b.cost || 0)
      return sortOrder === 'asc' ? priceA - priceB : priceB - priceA
    })
  }, [inventory, searchQuery, sortOrder])

  const handleSubmit = () => {
    setErrorText('')
    const totalBet = parsedCrystal + itemsTotal

    if (!parsedCrystal && selectedItems.length === 0) {
      setErrorText(t('royale.betModal.errorNoSelection'))
      return
    }

    if (totalBet < MIN_BET_TOTAL) {
      setErrorText(t('royale.betModal.missingAmount', { amount: (MIN_BET_TOTAL - totalBet).toLocaleString() }))
      return
    }

    if (parsedCrystal < 0 || parsedCrystal > Number(balance)) {
      setErrorText(t('royale.betModal.errorExceedsBalance'))
      return
    }

    if (onSubmit) {
      onSubmit({ crystal: parsedCrystal, items: selectedIds, total: totalBet })
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4">
      <div className="w-full max-w-3xl bg-dark-primary rounded-2xl border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">{t('royale.betModal.title')}</h3>
          <button onClick={onClose} className="p-2 text-text-secondary hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={`p-4 space-y-6 max-h-[80vh] overflow-y-auto ${totalBet > 0 ? 'pb-28' : ''}`}>
          <div className="p-0">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-semibold inline-flex items-center gap-2">
                {t('royale.betModal.crystalsTitle')}
              </h4>
              <span className="text-xs text-text-secondary inline-flex items-center gap-1">
                <Gem className="w-3.5 h-3.5 text-accent-primary" />
                <span className="text-white font-semibold">
                  {Number(balance || 0).toLocaleString()}
                </span>
              </span>
            </div>
            <div className="flex flex-col gap-3">
              {Number(balance || 0) > 0 ? (
                <>
                  <input
                    type="number"
                    min="0"
                    value={crystalValue}
                    onChange={(event) => setCrystalValue(normalizeCrystalValue(event.target.value))}
                    placeholder={t('royale.betModal.inputPlaceholder')}
                    className="w-full bg-dark-primary rounded-lg border border-white/10 px-3 py-2 text-white outline-none"
                  />
                  <div className="flex flex-wrap gap-2">
                    
                    {[100, 500, 1000, 10000].map((value) => (
                      <button
                        key={value}
                        onClick={() => handleQuickCrystal(parsedCrystal + value)}
                        className="px-3 py-2 rounded-lg bg-surface text-text-secondary hover:text-white border border-white/10"
                      >
                        +{value}
                      </button>
                    ))}
                    {[
                      { label: '1/4', value: 0.25 },
                      { label: '1/2', value: 0.5 },
                      { label: '3/4', value: 0.75 }
                    ].map((option) => (
                      <button
                        key={option.label}
                        onClick={() => handleQuickCrystal(Math.floor(Number(balance || 0) * option.value))}
                        className="px-3 py-2 rounded-lg bg-surface text-text-secondary hover:text-white border border-white/10"
                      >
                        {option.label}
                      </button>
                    ))}
                    <button
                      onClick={() => handleQuickCrystal(balance)}
                      className="px-3 py-2 rounded-lg bg-surface text-text-secondary hover:text-white border border-white/10"
                    >
                      {t('royale.betModal.maxLabel')}
                    </button>
                    <button
                      onClick={() => handleQuickCrystal('')}
                      className="px-3 py-2 rounded-lg bg-surface text-text-secondary hover:text-white border border-white/10"
                    >
                      <Delete className="w-4 h-4" />
                    </button>
                  </div>
                </>
              ) : null}
              {Number(balance || 0) <= 0 && (
                <button
                  onClick={() => navigate('/payment')}
                  className="w-full px-4 py-2 rounded-lg bg-surface text-text-secondary hover:text-white border border-white/10"
                >
                  {t('royale.betModal.topUpBalance')}
                </button>
              )}
            </div>
          </div>

          <div className="h-px bg-white/10 my-2" />

          <div className="p-0">
            <div className="flex flex-col gap-3 mb-3">
              <h4 className="text-white font-semibold">{t('royale.betModal.itemsTitle')}</h4>
              {inventory.length > 0 && (
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                    <input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder={t('royale.betModal.searchPlaceholder')}
                      className="w-full bg-dark-primary rounded-lg border border-white/10 pl-9 pr-3 py-2 text-white outline-none"
                    />
                  </div>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 rounded-lg bg-dark-primary border border-white/10 text-text-secondary hover:text-white"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {inventory.length > 0 ? (
              <div className="grid grid-cols-3 gap-2 lg:grid-cols-6">
                {filteredInventory.map((item) => {
                  const isSelected = selectedIds.includes(item.id)
                  const itemPrice = Number(item.price ?? item.cost ?? 0)
                  return (
                    <button
                      key={item.id}
                      onClick={() => toggleItem(item.id)}
                      className={`relative bg-surface rounded-lg border transition-all ${
                        isSelected
                          ? 'border-accent-primary shadow-lg shadow-accent-primary/30'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-1 left-1 z-10">
                          <div className="w-5 h-5 rounded-full bg-accent-primary flex items-center justify-center">
                            <CheckCircle2 size={12} className="text-dark-primary" />
                          </div>
                        </div>
                      )}

                      <div className="aspect-square p-2 flex items-center justify-center">
                        <img
                          src={item.img}
                          alt={item.name}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>

                      <div className="px-2 pb-2">
                        <p className="text-xs text-white font-medium truncate">
                          {String(item.name || '').split('|')[0].trim()}
                        </p>
                        <div className="flex items-center justify-center gap-1 mt-1">
                          <Gem size={12} className="text-accent-primary" />
                          <span className="text-xs font-bold text-accent-primary">
                            {itemPrice.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-white/10 bg-surface/40 p-6 text-center">
                <p className="text-white font-semibold mb-2">{t('royale.betModal.emptyTitle')}</p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <button
                    onClick={() => navigate('/market')}
                  className="w-full px-4 py-2 rounded-lg bg-surface text-text-secondary hover:text-white border border-white/10"
                  >
                    {t('royale.betModal.emptyMarketButton')}
                  </button>
                  <button
                    onClick={() => navigate('/cases')}
                  className="w-full px-4 py-2 rounded-lg bg-surface text-text-secondary hover:text-white border border-white/10"
                  >
                    {t('royale.betModal.emptyCasesButton')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {totalBet > 0 && (
          <div className="fixed bottom-0 left-0 right-0 z-10">
            <div className="max-w-3xl mx-auto px-4 pb-4">
              <div className="p-0">
                {errorText && <p className="text-red-400 text-xs mb-3">{errorText}</p>}
                <AnimatedButton
                  onClick={handleSubmit}
                  disabled={isLoading || !canSubmit}
                  className={isLoading || !canSubmit ? 'bg-surface text-text-secondary border border-white/10 cursor-not-allowed' : ''}
                >
                  {isLoading ? (
                    t('royale.betModal.submitting')
                  ) : !canSubmit ? (
                    t('royale.betModal.missingAmount', { amount: missingAmount.toLocaleString() })
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      {t('royale.betModal.submit')}
                      <span className="inline-flex items-center gap-1">
                        <Gem className="w-5 h-5 text-dark-primary" />
                        {(parsedCrystal + itemsTotal).toLocaleString()}
                      </span>
                    </span>
                  )}
                </AnimatedButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BetModal
